// Header - the firm, the page's trail, cmd-K trigger, connections pill, team, account.
// Ported from project/duralex-prototype-v2.html TopBar component.

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFirmIdentity } from "../../api/attorneyConfig";
import { TOKEN_STORAGE_KEY } from "../../api/auth";
import { AccountMenu } from "./AccountMenu";
import { ConnectionsPill } from "./ConnectionsPill";
import { NotificationsBell } from "./NotificationsBell";
import { TeamDropdown } from "./TeamDropdown";
import { SearchTrigger } from "./SearchTrigger";
import { TrailNav } from "./TrailNav";
import { titleOf, useTrail } from "./trail";

interface Props {
  onOpenSearch: () => void;
  onOpenDrawer?: () => void;
}

/**
 * Lightweight check for an attacker-tampered-or-missing JWT. v1 doesn't
 * gate routes on auth state - backend 401s drive a redirect via the axios
 * interceptor - so the only UX cost of being signed-out is a missing
 * affordance to sign back in. This little chip in the TopBar plugs that.
 */
function useHasToken(): boolean {
  const [hasToken, setHasToken] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return !!window.localStorage.getItem(TOKEN_STORAGE_KEY);
  });
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === TOKEN_STORAGE_KEY) setHasToken(!!e.newValue);
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return hasToken;
}

export function TopBar({ onOpenSearch, onOpenDrawer }: Props) {
  // Real config, not the fixture: no pill at all beats a made-up firm name.
  const firmName = useFirmIdentity().data?.firmName ?? "";
  const hasToken = useHasToken();
  const { pathname } = useLocation();
  const steps = useTrail(pathname);
  const title = titleOf(steps);
  useEffect(() => {
    document.title = title;
  }, [title]);
  // The chrome's last page must not stay in the tab after a sign-out.
  useEffect(
    () => () => {
      document.title = "DuraLex";
    },
    [],
  );
  return (
    <header className="cc-topbar">
      {onOpenDrawer && (
        <button
          type="button"
          className="cc-hamburger"
          aria-label="Open menu"
          onClick={onOpenDrawer}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6" strokeLinecap="round" />
            <line x1="4" y1="12" x2="20" y2="12" strokeLinecap="round" />
            <line x1="4" y1="18" x2="20" y2="18" strokeLinecap="round" />
          </svg>
        </button>
      )}
      <div className="cc-topbar-lead">
        {firmName && (
          <span className="firm-pill" title={firmName}>
            {firmName}
          </span>
        )}
        <TrailNav steps={steps} />
      </div>

      <SearchTrigger onOpen={onOpenSearch} />

      <div className="cc-topbar-right">
        <NotificationsBell />
        <TeamDropdown />
        <ConnectionsPill />
        {hasToken ? (
          <AccountMenu />
        ) : (
          <Link
            to="/login"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "6px 14px",
              borderRadius: "var(--r-pill)",
              background: "var(--action)",
              color: "var(--on-action)",
              fontSize: 12,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            Sign in
          </Link>
        )}
      </div>
    </header>
  );
}
